import {Component} from 'preact';
import SearchService from '@services/SearchService';
import Icon from '@components/common/Icon';
import {buildWebsiteSearchUrl, getWebsiteSearchParams} from '../../../containers/Search/searchParams';
import {CommerceItemLists, SearchTypes, SuggestionTypes} from '@upi/data-layer-constants';
import {
    getManualSearchType,
    saveSearchAnalyticsContext
} from '../../../containers/Search/searchAnalyticsContext';
import dataLayer from '@upi/data-layer';

const AUTO_COMPLETE_MIN_CHARACTERS = 2;
const AUTO_COMPLETE_TOP = 10;
const RECENT_SEARCH_MATCH_LIMIT = 3;
const PRODUCT_SUGGESTION_TOP = 5;
const PRODUCT_SUGGESTION_DEBOUNCE_MS = 200;
const LISTBOX_ID = 'search-suggestions-listbox--header';
const REQUEST_STATUS_IDLE = 'idle';
const REQUEST_STATUS_LOADING = 'loading';
const REQUEST_STATUS_SUCCESS = 'success';
const REQUEST_STATUS_ERROR = 'error';

function syncHeaderQueryValue ( searchInput ) {
    const isWebsiteSearchPage = /^\/(search|serach)(\/)?$/i.test( window.location.pathname );

    if ( !isWebsiteSearchPage ) {
        return;
    }

    const {term} = getWebsiteSearchParams( window.location.search );
    searchInput.value = term;
}

function getSuggestionText ( suggestion ) {
    return suggestion && (suggestion.queryPlusText || suggestion.text || '');
}

function mergeTextSuggestions ( recentSuggestions, autoCompleteSuggestions ) {
    const seenSuggestions = new Set();

    return (recentSuggestions || []).concat( autoCompleteSuggestions || [] ).filter( suggestion => {
        const suggestionText = String( getSuggestionText( suggestion ) || '' ).trim().toLowerCase();

        if ( !suggestionText || seenSuggestions.has( suggestionText ) ) {
            return false;
        }

        seenSuggestions.add( suggestionText );
        return true;
    } );
}

function createSvgIcon ( iconPathD, className ) {
    return (
        <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
            className={className}>
            <path d={iconPathD} fill="currentColor"/>
        </svg>
    );
}

function createHistoryIcon () {
    return createSvgIcon(
        'M12 1.75a10.25 10.25 0 1 0 7.247 17.5.75.75 0 0 0-1.06-1.06A8.75 8.75 0 1 1 20.75 12a.75.75 0 0 0 1.5 0A10.25 10.25 0 0 0 12 1.75Zm-.75 4.5a.75.75 0 0 1 1.5 0v5.19l3.28 1.89a.75.75 0 1 1-.75 1.3l-3.66-2.11a.75.75 0 0 1-.37-.65V6.25Z',
        'w-6 h-6 text-grey-darkest mr-3 flex-no-shrink'
    );
}

function createCloseIcon () {
    return createSvgIcon(
        'M6.22 6.22a.75.75 0 0 1 1.06 0L12 10.94l4.72-4.72a.75.75 0 1 1 1.06 1.06L13.06 12l4.72 4.72a.75.75 0 1 1-1.06 1.06L12 13.06l-4.72 4.72a.75.75 0 1 1-1.06-1.06L10.94 12 6.22 7.28a.75.75 0 0 1 0-1.06Z',
        'w-8 h-8 -mr-2 text-grey-darkest'
    );
}

function buildSuggestionLabel ( suggestionText, query ) {
    const normalizedText = String( suggestionText || '' );
    const normalizedQuery = String( query || '' ).trim();
    const normalizedTextLower = normalizedText.toLowerCase();
    const normalizedQueryLower = normalizedQuery.toLowerCase();
    const prefixIndex = normalizedQueryLower ? normalizedTextLower.indexOf( normalizedQueryLower ) : -1;

    if ( prefixIndex < 0 || !normalizedQuery ) {
        return {
            prefix: normalizedText,
            completion: ''
        };
    }

    return {
        prefix: normalizedText.slice( 0, prefixIndex + normalizedQuery.length ),
        completion: normalizedText.slice( prefixIndex + normalizedQuery.length )
    };
}

function getProductImageSource ( product ) {
    return product?.images?.[0]?.source || null;
}

function normalizeDocLinks ( docs ) {
    return (docs || [])
        .map( doc => ({title: String( doc.title || '' ), url: doc?.document?.source || null}) )
        .filter( ({url}) => !!url );
}

function toSdsResourceUrl ( url ) {
    const key = url.split( '/' ).pop();
    return `/Resources/GetSds?key=${key}`;
}

function filterDocLinksByLocale ( links, locale ) {
    if ( !locale || links.length === 0 ) {
        return links;
    }
    const suffix = `-${locale.toLowerCase()}`;
    const localeMatches = links.filter( ({url}) => url.toLowerCase().endsWith( suffix ) );
    return localeMatches.length > 0 ? localeMatches : links;
}

class HeaderSearchSuggestions extends Component {
    requestId = 0;
    recentSuggestionRequestId = 0;
    debounceTimer = null;
    suggestionAbortController = null;
    productSuggestionsPreparationPromise = null;
    recentSuggestions = [];
    recentSuggestionsQuery = null;
    autoCompleteSuggestions = [];
    autoCompleteSuggestionsQuery = null;
    activeIndex = -1;
    _isMounted = false;

    state = {
        activeIndex: -1,
        suggestions: [],
        suggestionsStatus: REQUEST_STATUS_IDLE,
        productSuggestions: [],
        productSuggestionsStatus: REQUEST_STATUS_IDLE,
        openDocMenu: null
    };

    componentDidMount () {
        const {searchInput} = this.props;

        this._isMounted = true;

        syncHeaderQueryValue( searchInput );
        searchInput.setAttribute( 'autocomplete', 'off' );
        if ( this.props.suggestionsEnabled !== false ) {
            searchInput.setAttribute( 'role', 'combobox' );
            searchInput.setAttribute( 'aria-autocomplete', 'list' );
            searchInput.setAttribute( 'aria-controls', LISTBOX_ID );
            searchInput.setAttribute( 'aria-expanded', 'false' );
        }

        this.boundHandleInput = this.handleInput.bind( this );
        this.boundHandleFocus = this.handleFocus.bind( this );
        this.boundHandleKeyDown = this.handleKeyDown.bind( this );
        this.boundHandleDocumentClick = this.handleDocumentClick.bind( this );
        this.boundHandleSearchButtonClick = this.handleSearchButtonClick.bind( this );
        this.boundHandleClearButtonClick = this.handleClearButtonClick.bind( this );
        this.boundHandleViewportChange = this.handleViewportChange.bind( this );

        searchInput.addEventListener( 'input', this.boundHandleInput );
        searchInput.addEventListener( 'focus', this.boundHandleFocus );
        searchInput.addEventListener( 'keydown', this.boundHandleKeyDown );
        document.addEventListener( 'click', this.boundHandleDocumentClick );
        window.addEventListener( 'resize', this.boundHandleViewportChange );

        const {searchBoxWrapper} = this.props;
        const searchButton = searchBoxWrapper.querySelector( '#search-submit--header' );
        const clearButton = searchBoxWrapper.querySelector( '#search-clear--header' );

        if ( searchButton ) {
            searchButton.addEventListener( 'click', this.boundHandleSearchButtonClick );
            this.searchButton = searchButton;
        }

        if ( clearButton ) {
            clearButton.addEventListener( 'click', this.boundHandleClearButtonClick );
            this.clearButton = clearButton;
        }
    }

    componentWillUnmount () {
        const {searchInput} = this.props;

        this._isMounted = false;

        if ( this.debounceTimer ) {
            clearTimeout( this.debounceTimer );
            this.debounceTimer = null;
        }

        this.invalidatePendingSuggestionRequest();
        this.invalidatePendingRecentSuggestionRequest();

        searchInput.removeEventListener( 'input', this.boundHandleInput );
        searchInput.removeEventListener( 'focus', this.boundHandleFocus );
        searchInput.removeEventListener( 'keydown', this.boundHandleKeyDown );
        document.removeEventListener( 'click', this.boundHandleDocumentClick );
        window.removeEventListener( 'resize', this.boundHandleViewportChange );

        if ( this.searchButton ) {
            this.searchButton.removeEventListener( 'click', this.boundHandleSearchButtonClick );
        }

        if ( this.clearButton ) {
            this.clearButton.removeEventListener( 'click', this.boundHandleClearButtonClick );
        }
    }

    componentDidUpdate () {
        if ( this.props.suggestionsEnabled === false ) {
            return;
        }

        const {searchInput} = this.props;
        const isExpanded = this.state.suggestions.length > 0;

        searchInput.setAttribute( 'aria-expanded', String( isExpanded ) );

        if ( this.state.activeIndex >= 0 && this.state.suggestions[this.state.activeIndex] ) {
            searchInput.setAttribute( 'aria-activedescendant', `${LISTBOX_ID}-option-${this.state.activeIndex}` );
        }
        else {
            searchInput.removeAttribute( 'aria-activedescendant' );
        }

        this.trackProductSuggestionImpression();
    }

    async navigateToSearch ( value, searchType = getManualSearchType() ) {
        const searchValue = String( value || '' ).trim();
        const searchUrl = buildWebsiteSearchUrl( searchValue );

        if ( searchUrl === '/search' ) {
            return false;
        }

        await SearchService.saveRecentSearch( searchValue );
        saveSearchAnalyticsContext( searchValue, searchType );
        this.props.navigate( searchUrl );
        return true;
    }

    handleSearchButtonClick () {
        const {searchInput} = this.props;

        this.navigateToSearch( searchInput.value );
    }

    handleClearButtonClick () {
        const {searchInput} = this.props;

        searchInput.value = '';
        searchInput.focus();
        this.closeSuggestions();
    }

    handleDocumentClick ( event ) {
        const {searchBoxWrapper} = this.props;

        if ( !searchBoxWrapper.contains( event.target ) ) {
            this.closeSuggestions();
            return;
        }

        if ( this.state.openDocMenu !== null && !event.target.closest( '[data-doc-menu]' ) ) {
            this.setState( {openDocMenu: null} );
        }
    }

    handleFocus () {
        if ( this.props.suggestionsEnabled === false ) {
            return;
        }

        const {searchInput} = this.props;

        this.prepareProductSuggestions();
        this.fetchRecentSuggestions( searchInput.value );

        if ( this.isSuggestionLayerVisible() ) {
            this.forceUpdate();
        }
    }

    prepareProductSuggestions () {
        if ( this.productSuggestionsPreparationPromise ) {
            return;
        }

        const preparationPromise = SearchService.prepareProductSuggestions();
        this.productSuggestionsPreparationPromise = preparationPromise;

        preparationPromise.catch( error => {
            if ( this.productSuggestionsPreparationPromise === preparationPromise ) {
                this.productSuggestionsPreparationPromise = null;
            }
            console.error( '[SearchSuggestions] Unable to prepare product suggestions', error );
        } );
    }

    handleViewportChange () {
        if ( this._isMounted ) {
            this.forceUpdate();
        }
    }

    handleInput ( event ) {
        if ( this.props.suggestionsEnabled === false ) {
            return;
        }

        const value = String( event.target.value || '' ).trim();

        this.activeIndex = -1;
        this.setState( {activeIndex: -1} );

        if ( this.debounceTimer ) {
            clearTimeout( this.debounceTimer );
            this.debounceTimer = null;
        }

        this.invalidatePendingSuggestionRequest();
        this.invalidatePendingRecentSuggestionRequest();
        this.recentSuggestions = [];
        this.recentSuggestionsQuery = null;
        this.autoCompleteSuggestions = [];
        this.autoCompleteSuggestionsQuery = null;

        this.fetchRecentSuggestions( value );

        if ( value.length < AUTO_COMPLETE_MIN_CHARACTERS ) {
            this.setState( {
                suggestionsStatus: REQUEST_STATUS_IDLE,
                productSuggestions: [],
                productSuggestionsStatus: REQUEST_STATUS_IDLE,
                openDocMenu: null
            } );
            return;
        }

        const currentRequestId = this.requestId;

        this.fetchAutoCompleteSuggestions( value, currentRequestId );

        this.debounceTimer = setTimeout( () => {
            this.debounceTimer = null;
            this.fetchProductSuggestions( value, currentRequestId );
        }, PRODUCT_SUGGESTION_DEBOUNCE_MS );
    }

    handleKeyDown ( event ) {
        const {searchInput} = this.props;
        const {
            suggestions
        } = this.state;
        const activeIndex = this.activeIndex;

        if ( event.key === 'ArrowDown' ) {
            event.preventDefault();
            this.moveActiveIndex( 1 );
            return;
        }

        if ( event.key === 'ArrowUp' ) {
            event.preventDefault();
            this.moveActiveIndex( -1 );
            return;
        }

        if ( event.key === 'Escape' ) {
            this.closeSuggestions();
            return;
        }

        if ( event.key !== 'Enter' ) {
            return;
        }

        if ( activeIndex >= 0 && suggestions[activeIndex] ) {
            event.preventDefault();
            this.applySuggestion( activeIndex );
            return;
        }

        event.preventDefault();
        this.navigateToSearch( searchInput.value );
    }

    fetchAutoCompleteSuggestions ( query, currentRequestId ) {
        const abortController = typeof AbortController === 'undefined' ? null : new AbortController();

        this.suggestionAbortController = abortController;
        this.activeIndex = -1;
        this.setState( {
            suggestionsStatus: REQUEST_STATUS_LOADING,
            activeIndex: -1,
            openDocMenu: null
        } );

        SearchService.getAutoCompleteSuggestions( query, {
            top: AUTO_COMPLETE_TOP,
            signal: abortController?.signal
        } ).then( suggestions => {
            if ( !this.isCurrentSuggestionRequest( currentRequestId, query ) ) {
                return;
            }

            this.autoCompleteSuggestions = suggestions.filter( suggestion => !!getSuggestionText( suggestion ) );
            this.autoCompleteSuggestionsQuery = query;
            this.setState( {
                suggestions: this.getTextSuggestionsForQuery( query ),
                suggestionsStatus: REQUEST_STATUS_SUCCESS,
                activeIndex: -1
            } );
            this.activeIndex = -1;
        } ).catch( error => {
            if ( abortController?.signal.aborted || !this.isCurrentSuggestionRequest( currentRequestId, query ) ) {
                return;
            }

            this.autoCompleteSuggestions = [];
            this.autoCompleteSuggestionsQuery = query;
            this.setState( {
                suggestions: this.getTextSuggestionsForQuery( query ),
                suggestionsStatus: REQUEST_STATUS_ERROR,
                activeIndex: -1
            } );
            this.activeIndex = -1;
            console.error( '[SearchSuggestions] Unable to load autocomplete suggestions', error );
        } ).finally( () => {
            if ( this.suggestionAbortController === abortController ) {
                this.suggestionAbortController = null;
            }
        } );
    }

    fetchProductSuggestions ( query, currentRequestId ) {
        if ( !this.isCurrentSuggestionRequest( currentRequestId, query ) ) {
            return;
        }

        this.setState( {
            productSuggestionsStatus: REQUEST_STATUS_LOADING,
            openDocMenu: null
        } );

        SearchService.getBestMatchingProducts( query, {top: PRODUCT_SUGGESTION_TOP} )
            .then( productSuggestions => {
                if ( !this.isCurrentSuggestionRequest( currentRequestId, query ) ) {
                    return;
                }

                this.setState( {
                    productSuggestions: productSuggestions || [],
                    productSuggestionsStatus: REQUEST_STATUS_SUCCESS
                } );
            } )
            .catch( error => {
                if ( !this.isCurrentSuggestionRequest( currentRequestId, query ) ) {
                    return;
                }

                this.setState( {
                    productSuggestions: [],
                    productSuggestionsStatus: REQUEST_STATUS_ERROR
                } );
                console.error( '[SearchSuggestions] Unable to load product suggestions', error );
            } );
    }

    fetchRecentSuggestions ( query ) {
        const normalizedQuery = String( query || '' ).trim();
        const currentRequestId = ++this.recentSuggestionRequestId;

        SearchService.getRecentSearchSuggestions( normalizedQuery, {
            top: RECENT_SEARCH_MATCH_LIMIT
        } ).then( suggestions => {
            if ( !this.isCurrentRecentSuggestionRequest( currentRequestId, normalizedQuery ) ) {
                return;
            }

            this.recentSuggestions = (suggestions || []).filter( suggestion => !!getSuggestionText( suggestion ) );
            this.recentSuggestionsQuery = normalizedQuery;

            const nextSuggestions = this.getTextSuggestionsForQuery( normalizedQuery );
            const shouldRetainVisibleSuggestions = normalizedQuery.length >= AUTO_COMPLETE_MIN_CHARACTERS
                && nextSuggestions.length === 0
                && (this.debounceTimer || this.state.suggestionsStatus === REQUEST_STATUS_LOADING);

            if ( shouldRetainVisibleSuggestions ) {
                return;
            }

            this.activeIndex = -1;
            this.setState( {
                suggestions: nextSuggestions,
                activeIndex: -1
            } );
        } ).catch( error => {
            if ( !this.isCurrentRecentSuggestionRequest( currentRequestId, normalizedQuery ) ) {
                return;
            }

            this.recentSuggestions = [];
            this.recentSuggestionsQuery = normalizedQuery;

            if ( normalizedQuery.length < AUTO_COMPLETE_MIN_CHARACTERS ) {
                this.activeIndex = -1;
                this.setState( {
                    suggestions: this.getTextSuggestionsForQuery( normalizedQuery ),
                    activeIndex: -1
                } );
            }

            console.error( '[SearchSuggestions] Unable to load recent search suggestions', error );
        } );
    }

    getTextSuggestionsForQuery ( query ) {
        const recentSuggestions = this.recentSuggestionsQuery === query ? this.recentSuggestions : [];
        const autoCompleteSuggestions = this.autoCompleteSuggestionsQuery === query
            ? this.autoCompleteSuggestions
            : [];

        return mergeTextSuggestions( recentSuggestions, autoCompleteSuggestions );
    }

    isCurrentSuggestionRequest ( requestId, query ) {
        const {searchInput} = this.props;

        return this._isMounted
            && requestId === this.requestId
            && String( searchInput.value || '' ).trim() === query;
    }

    isCurrentRecentSuggestionRequest ( requestId, query ) {
        const {searchInput} = this.props;

        return this._isMounted
            && requestId === this.recentSuggestionRequestId
            && String( searchInput.value || '' ).trim() === query;
    }

    cancelPendingSuggestionRequest () {
        if ( this.suggestionAbortController ) {
            this.suggestionAbortController.abort();
            this.suggestionAbortController = null;
        }
    }

    invalidatePendingSuggestionRequest () {
        this.requestId += 1;
        this.cancelPendingSuggestionRequest();
    }

    invalidatePendingRecentSuggestionRequest () {
        this.recentSuggestionRequestId += 1;
    }

    moveActiveIndex ( offset ) {
        const {
            suggestions,
            activeIndex
        } = this.state;

        if ( suggestions.length === 0 ) {
            return;
        }

        const nextIndex = activeIndex < 0
            ? (offset > 0 ? 0 : suggestions.length - 1)
            : (activeIndex + offset + suggestions.length) % suggestions.length;

        this.activeIndex = nextIndex;
        this.setState( {activeIndex: nextIndex} );
    }

    async applySuggestion ( index ) {
        const {searchInput} = this.props;
        const suggestion = this.state.suggestions[index];
        const suggestionText = getSuggestionText( suggestion );

        if ( !suggestionText ) {
            return;
        }

        dataLayer.search.sendSearchSuggestionClick( {
            searchTerm: searchInput.value,
            suggestionText,
            suggestionType: SuggestionTypes.QUERY,
            suggestionPosition: index + 1
        } );
        searchInput.value = suggestionText;
        this.closeSuggestions();
        await this.navigateToSearch( suggestionText, SearchTypes.SUGGESTION );
    }

    async removeRecentSuggestion ( index ) {
        const suggestion = this.state.suggestions[index];
        const suggestionText = getSuggestionText( suggestion );

        if ( !suggestion || !suggestion.isRecentSearch || !suggestionText ) {
            return;
        }

        await SearchService.removeRecentSearch( suggestionText );
        this.recentSuggestions = this.recentSuggestions.filter(
            item => getSuggestionText( item ).toLowerCase() !== suggestionText.toLowerCase()
        );
        this.setState( prevState => {
            const nextSuggestions = prevState.suggestions.filter( ( item, itemIndex ) => itemIndex !== index );
            const nextActiveIndex = prevState.activeIndex >= nextSuggestions.length
                ? nextSuggestions.length - 1
                : prevState.activeIndex;
            const normalizedActiveIndex = nextSuggestions.length > 0 ? nextActiveIndex : -1;

            this.activeIndex = normalizedActiveIndex;

            return {
                suggestions: nextSuggestions,
                productSuggestions: prevState.productSuggestions,
                activeIndex: normalizedActiveIndex
            };
        } );
    }

    closeSuggestions () {
        if ( this.debounceTimer ) {
            clearTimeout( this.debounceTimer );
            this.debounceTimer = null;
        }

        this.invalidatePendingSuggestionRequest();
        this.invalidatePendingRecentSuggestionRequest();
        this.recentSuggestions = [];
        this.recentSuggestionsQuery = null;
        this.autoCompleteSuggestions = [];
        this.autoCompleteSuggestionsQuery = null;
        this.activeIndex = -1;
        this.trackedProductSuggestionSignature = null;
        this.setState( {
            suggestions: [],
            suggestionsStatus: REQUEST_STATUS_IDLE,
            productSuggestions: [],
            productSuggestionsStatus: REQUEST_STATUS_IDLE,
            activeIndex: -1,
            openDocMenu: null
        } );
    }

    trackProductSuggestionImpression () {
        const {productSuggestions} = this.state;
        const isDesktop = this.isDesktopViewport();

        if ( !isDesktop || productSuggestions.length === 0 ) {
            return;
        }

        const signature = productSuggestions.map( product => product.sku ).join( ',' );
        if ( signature === this.trackedProductSuggestionSignature ) {
            return;
        }

        const visibleProducts = productSuggestions.map( ( product, index ) => ({
            ...product,
            position: index + 1
        }) );

        dataLayer.product.sendProductListView( visibleProducts, CommerceItemLists.SUGGESTED_PRODUCTS );
        this.trackedProductSuggestionSignature = signature;
    }

    isDesktopViewport () {
        return !window.matchMedia || window.matchMedia( '(min-width: 768px)' ).matches;
    }

    isSuggestionLayerVisible () {
        const {suggestions, productSuggestions} = this.state;

        return suggestions.length > 0
            || (this.isDesktopViewport() && productSuggestions.length > 0);
    }

    trackSuggestedProductClick ( product, index ) {
        dataLayer.product.sendProductClick( {...product, position: index + 1}, {
            itemList: CommerceItemLists.SUGGESTED_PRODUCTS,
            searchTerm: this.props.searchInput.value
        } );
    }

    renderSuggestion ( suggestion, index ) {
        const {searchInput} = this.props;
        const suggestionText = getSuggestionText( suggestion );
        const isActive = index === this.state.activeIndex;
        const itemClassName = `${isActive ? 'bg-grey-lightest' : 'bg-white'} hover:bg-grey-lightest`;

        if ( suggestion.isRecentSearch ) {
            return (
                <li key={`${suggestionText}-${index}`} className={itemClassName}>
                    <div className="flex items-center px-4">
                        <button
                            type="button"
                            id={`${LISTBOX_ID}-option-${index}`}
                            data-suggestion-index={String( index )}
                            role="option"
                            aria-selected={isActive ? 'true' : 'false'}
                            className="flex items-center flex-1 w-full text-left bg-transparent overflow-hidden"
                            onMouseDown={event => event.preventDefault()}
                            onMouseMove={() => {
                                this.activeIndex = index;
                                this.setState( {activeIndex: index} );
                            }}
                            onClick={() => this.applySuggestion( index )}>
                            <span
                                className="flex items-center flex-1 w-full text-body text-grey-darker overflow-hidden">
                                {createHistoryIcon()}
                                <span className="truncate">{suggestionText}</span>
                            </span>
                        </button>
                        <button
                            type="button"
                            data-remove-recent-query={String( index )}
                            aria-label={`Remove recent search ${suggestionText}`}
                            tabIndex="-1"
                            className="ml-3 p-1 text-grey hover:text-black focus:text-black"
                            onMouseDown={event => event.preventDefault()}
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                this.removeRecentSuggestion( index );
                            }}>
                            {createCloseIcon()}
                        </button>
                    </div>
                </li>
            );
        }

        const {
            prefix,
            completion
        } = buildSuggestionLabel( suggestionText, searchInput.value );

        return (
            <li key={`${suggestionText}-${index}`} className={itemClassName}>
                <button
                    type="button"
                    id={`${LISTBOX_ID}-option-${index}`}
                    data-suggestion-index={String( index )}
                    role="option"
                    aria-selected={isActive ? 'true' : 'false'}
                    className="flex items-center w-full px-4 text-left bg-transparent"
                    onMouseDown={event => event.preventDefault()}
                    onMouseMove={() => {
                        this.activeIndex = index;
                        this.setState( {activeIndex: index} );
                    }}
                    onClick={() => this.applySuggestion( index )}>
                    <span className="block truncate text-body text-grey-darker">
                        <span>{prefix}</span>
                        {completion
                            ? <strong className="font-semibold text-black">{completion}</strong>
                            : null}
                    </span>
                </button>
            </li>
        );
    }

    renderDocAction ( docs, label, productIndex, type ) {
        const allLinks = normalizeDocLinks( docs );
        const filteredLinks = type === 'sds'
            ? filterDocLinksByLocale( allLinks, upi.conf.locale )
            : allLinks;
        const links = type === 'sds'
            ? filteredLinks.map( link => ({...link, url: toSdsResourceUrl( link.url )}) )
            : filteredLinks;

        if ( links.length === 0 ) {
            return null;
        }

        const linkClassName = 'text-sm text-grey-darkest underline hover:text-black';

        if ( links.length === 1 ) {
            return (
                <a
                    href={links[0].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${linkClassName} mr-3`}
                    onMouseDown={event => event.preventDefault()}>
                    {label}
                </a>
            );
        }

        const isOpen = this.state.openDocMenu !== null
            && this.state.openDocMenu.index === productIndex
            && this.state.openDocMenu.type === type;

        return (
            <div className="relative mr-3" data-doc-menu="true">
                <button
                    type="button"
                    className={`${linkClassName} bg-transparent cursor-pointer p-0 border-0`}
                    onMouseDown={event => event.preventDefault()}
                    onClick={event => {
                        event.stopPropagation();
                        this.setState( {openDocMenu: isOpen ? null : {index: productIndex, type}} );
                    }}>
                    {label}
                </button>
                {isOpen
                    ? <ul
                        className="absolute z-40 bg-white border border-solid border-grey-light shadow-md list-reset m-0 py-1 min-w-48"
                        style={{top: '100%', left: 0}}
                        data-doc-menu="true">
                        {links.map( ({title, url}, i) => (
                            <li key={i}>
                                <a
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block px-3 py-2 text-sm text-grey-darkest no-underline hover:bg-grey-lightest whitespace-no-wrap"
                                    onMouseDown={event => event.preventDefault()}
                                    onClick={() => this.setState( {openDocMenu: null} )}>
                                    {title}
                                </a>
                            </li>
                        ) )}
                    </ul>
                    : null}
            </div>
        );
    }

    renderTextSuggestionSkeleton () {
        return (
            <div
                data-testid="header-search-text-skeleton"
                aria-hidden="true"
                className="px-4 py-2">
                {['w-3/4', 'w-1/2', 'w-2/3'].map( ( width, index ) => (
                    <div key={index} className="py-3">
                        <span className={`block h-4 ${width} rounded bg-grey-lightest`}/>
                    </div>
                ) )}
            </div>
        );
    }

    renderProductSuggestionSkeleton () {
        return (
            <div data-testid="header-search-product-skeleton" aria-hidden="true">
                <span className="block h-4 w-1/2 rounded bg-grey-lightest mb-4"/>
                {[0, 1, 2].map( index => (
                    <div key={index} className="flex items-center mb-4 last:mb-0">
                        <span className="block w-16 h-16 mr-3 rounded bg-grey-lightest flex-no-shrink"/>
                        <span className="block flex-1 min-w-0">
                            <span className="block h-4 w-3/4 rounded bg-grey-lightest mb-3"/>
                            <span className="block h-3 w-1/2 rounded bg-grey-lightest"/>
                        </span>
                    </div>
                ) )}
            </div>
        );
    }

    render () {
        const {
            suggestions,
            suggestionsStatus,
            productSuggestions,
            productSuggestionsStatus
        } = this.state;
        const isEcommerceSite = upi.conf.isEcommerceSite;
        const isDesktop = this.isDesktopViewport();
        const hasSuggestions = suggestions.length > 0;
        const hasVisibleProducts = isDesktop && productSuggestions.length > 0;
        const showSuggestionSkeleton = isDesktop
            && suggestionsStatus === REQUEST_STATUS_LOADING
            && !hasSuggestions
            && hasVisibleProducts;
        const showProductSkeleton = isDesktop
            && productSuggestionsStatus === REQUEST_STATUS_LOADING
            && !hasVisibleProducts
            && hasSuggestions;
        const showSuggestionSection = hasSuggestions || showSuggestionSkeleton;
        const showProductSection = hasVisibleProducts || showProductSkeleton;
        const hasTwoColumns = showSuggestionSection && showProductSection;

        if ( !this.isSuggestionLayerVisible() ) {
            return null;
        }

        return (
            <div
                data-testid="header-search-suggestions"
                className="absolute pin-l pin-r bg-white border border-solid border-grey-light shadow-md z-30 md:min-w-lg"
                style={{
                    top: '100%',
                    marginTop: '0.5rem'
                }}>
                <div className="flex">
                    {showSuggestionSection
                        ? <div
                            data-testid="header-search-text-section"
                            aria-busy={String( showSuggestionSkeleton )}
                            className={hasTwoColumns ? 'w-full md:w-1/2' : 'w-full'}>
                            {showSuggestionSkeleton
                                ? this.renderTextSuggestionSkeleton()
                                : <ul id={LISTBOX_ID} role="listbox" className="list-reset m-0 p-0">
                                    {suggestions.map( ( suggestion, index ) => this.renderSuggestion(
                                        suggestion,
                                        index
                                    ) )}
                                </ul>}
                        </div>
                        : null}
                    {showProductSection
                        ? <aside
                            data-testid="header-search-product-section"
                            aria-busy={String( showProductSkeleton )}
                            className={`hidden md:block ${hasTwoColumns
                                ? 'md:w-1/2 border-l border-solid border-grey-lightest'
                                : 'md:w-full'} px-4 py-4`}>
                            {showProductSkeleton
                                ? this.renderProductSuggestionSkeleton()
                                : <div>
                                    <h3 className="text-body font-semibold text-grey-darkest mb-4">
                                        Best Matching Products
                                    </h3>
                                    <ul className="list-reset m-0 p-0">
                                        {productSuggestions.map( ( product, productIndex ) => {
                                            const imageSource = getProductImageSource( product );
                                            const anchorAttrs = isEcommerceSite
                                                ? {
                                                    'data-quick-view': true,
                                                    'data-sku': product.sku,
                                                    'data-item-list-id': CommerceItemLists.SUGGESTED_PRODUCTS.id,
                                                    'data-item-list-name': CommerceItemLists.SUGGESTED_PRODUCTS.name
                                                }
                                                : {};

                                            return (
                                                <li key={product.sku || product.url} className="mb-4 last:mb-0">
                                                    <div className="flex items-center text-current no-underline">
                                                        <div
                                                            className="w-16 h-16 mr-3 flex items-center justify-center bg-white flex-no-shrink">
                                                            {imageSource
                                                                ? <img src={imageSource} alt=""
                                                                       className="max-w-full max-h-full"/>
                                                                : <span className="text-body-sm text-grey-dark">
                                                                    Product
                                                                </span>}
                                                        </div>
                                                        <div className="flex-1 min-w-0 leading-snug">
                                                            <a href={product.url}
                                                               onClick={() => this.trackSuggestedProductClick(
                                                                   product,
                                                                   productIndex
                                                               )}
                                                               className="block mb-1 text-sm font-semibold text-grey-darkest">
                                                                {product.kitName}
                                                                {product.sku
                                                                    ? <span
                                                                        className="text-sm pl-1 font-normal text-grey-dark">#{product.sku}</span>
                                                                    : null}
                                                            </a>
                                                            <div className="flex items-center">
                                                                {isEcommerceSite
                                                                    ? <a href={product.url} {...anchorAttrs}
                                                                         className="flex-no-shrink mr-3"
                                                                         onMouseDown={event => event.preventDefault()}
                                                                         onClick={() => this.trackSuggestedProductClick(
                                                                             product,
                                                                             productIndex
                                                                         )}
                                                                         aria-label={`Add ${product.kitName} to cart`}>
                                                                        <Icon.ui name="shopping-cart-add"
                                                                                 className="text-xl"/>
                                                                    </a>
                                                                    : null}
                                                                {this.renderDocAction(
                                                                    product.sds,
                                                                    'SDS',
                                                                    productIndex,
                                                                    'sds'
                                                                )}
                                                                {this.renderDocAction(
                                                                    product.ifus,
                                                                    'IFU',
                                                                    productIndex,
                                                                    'ifu'
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        } )}
                                    </ul>
                                </div>}
                        </aside>
                        : null}
                </div>
            </div>
        );
    }
}

export default HeaderSearchSuggestions;
